import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    'panel', 'messageList', 'input', 'sendButton',
    'typingIndicator', 'conversationList', 'toggleButton',
    'headerTitle'
  ]

  static values = {
    conversationId: String,
    open: Boolean
  }

  connect() {
    this.restoreState()
    this.currentPath = window.location.pathname

    // Restore panel visibility
    if (this.openValue) {
      this.panelTarget.classList.add('assistant-panel-open')
      this.toggleButtonTarget.classList.add('assistant-button-hidden')
    }

    if (this.openValue && this.conversationIdValue) {
      // Load conversation messages, then check for page navigation
      this.loadConversationAndCheckNav()
    }

    if (this.openValue) {
      setTimeout(() => this.inputTarget.focus(), 200)
    }
  }

  async loadConversationAndCheckNav() {
    try {
      const data = await this.fetchJSON(`/assistant/conversations/${this.conversationIdValue}`)
      this.clearMessages()

      if (data.messages && data.messages.length > 0) {
        data.messages.forEach(msg => this.addMessage(msg.role, msg.content))
      } else {
        const ctx = this.buildContext()
        const name = ctx.user_name || 'there'
        this.addMessage('assistant', `Hi ${name}! I'm here to help you with anything on Givsum. What can I help you with?`)
      }

      // Check if user navigated to a new page
      const savedPath = this.getSavedPath()
      if (savedPath && savedPath !== this.currentPath) {
        this.addMessage('assistant', `I see you're now on a different page. I can help you with this page too!`)
      }

      this.saveCurrentPath()
      this.scrollToBottom()
    } catch (e) {
      // Conversation not found, start fresh
      this.conversationIdValue = ''
      this.persistState()
      this.newConversation()
    }
  }

  // ─── Panel Toggle ──────────────────────────────

  toggle() {
    this.openValue = !this.openValue
    this.panelTarget.classList.toggle('assistant-panel-open', this.openValue)
    this.toggleButtonTarget.classList.toggle('assistant-button-hidden', this.openValue)
    this.persistState()

    if (this.openValue && !this.conversationIdValue) {
      this.newConversation()
    }

    if (this.openValue) {
      setTimeout(() => this.inputTarget.focus(), 100)
    }
  }

  getSavedPath() {
    try {
      return JSON.parse(localStorage.getItem('givsum_assistant') || '{}').lastPath
    } catch (e) { return null }
  }

  saveCurrentPath() {
    try {
      const state = JSON.parse(localStorage.getItem('givsum_assistant') || '{}')
      state.lastPath = window.location.pathname
      localStorage.setItem('givsum_assistant', JSON.stringify(state))
    } catch (e) {}
  }

  close() {
    this.openValue = false
    this.panelTarget.classList.remove('assistant-panel-open')
    this.toggleButtonTarget.classList.remove('assistant-button-hidden')
    this.persistState()
  }

  // ─── Conversations ─────────────────────────────

  async newConversation() {
    const context = this.buildContext()
    try {
      const response = await this.fetchJSON('/assistant/conversations', {
        method: 'POST',
        body: JSON.stringify({ assistant_context: context })
      })
      this.conversationIdValue = response.id
      this.clearMessages()
      const ctx = this.buildContext()
      const name = ctx.user_name || 'there'
      this.addMessage('assistant', `Hi ${name}! I'm here to help you with anything on Givsum. What can I help you with?`)
      this.persistState()
    } catch (e) {
      console.error('Failed to create conversation:', e)
    }
  }

  async loadConversation(id) {
    try {
      const data = await this.fetchJSON(`/assistant/conversations/${id}`)
      this.conversationIdValue = data.id
      this.clearMessages()
      if (data.messages && data.messages.length > 0) {
        data.messages.forEach(msg => this.addMessage(msg.role, msg.content))
      } else {
        const ctx = this.buildContext()
      const name = ctx.user_name || 'there'
      this.addMessage('assistant', `Hi ${name}! I'm here to help you with anything on Givsum. What can I help you with?`)
      }
      this.scrollToBottom()
    } catch (e) {
      // Conversation not found, start a new one
      this.newConversation()
    }
  }

  // ─── Send Message & Stream Response ────────────

  async sendMessage(event) {
    if (event) event.preventDefault()

    const message = this.inputTarget.value.trim()
    if (!message) return

    // Ensure we have a conversation before sending
    if (!this.conversationIdValue) {
      await this.newConversation()
      if (!this.conversationIdValue) {
        this.addMessage('assistant', 'Unable to start a conversation. Please try again.')
        return
      }
    }

    // Disable input while streaming
    this.inputTarget.value = ''
    this.inputTarget.disabled = true
    this.sendButtonTarget.disabled = true

    // Show user message
    this.addMessage('user', message)
    this.scrollToBottom()

    // Show typing indicator
    this.typingIndicatorTarget.classList.remove('d-none')
    this.scrollToBottom()

    try {
      const context = this.buildContext()
      const response = await fetch('/assistant/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          conversation_id: this.conversationIdValue,
          message: message,
          assistant_context: context,
          page_context: this.buildPageContext()
        })
      })

      // Read SSE stream
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let assistantBubble = this.addMessage('assistant', '')
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6)
          if (!jsonStr) continue

          try {
            const chunk = JSON.parse(jsonStr)
            if (chunk.type === 'text') {
              // Buffer text and strip ---actions--- block from display
              const contentEl = assistantBubble.querySelector('.assistant-msg-content')
              const currentText = contentEl.textContent + chunk.content
              const actionsIdx = currentText.indexOf('---actions---')
              if (actionsIdx === -1) {
                contentEl.textContent = currentText
              } else {
                // Stop displaying once actions block starts
                contentEl.textContent = currentText.substring(0, actionsIdx).trim()
              }
              this.scrollToBottom()
            } else if (chunk.type === 'done') {
              // Render markdown in the final message (already stripped of actions)
              const content = assistantBubble.querySelector('.assistant-msg-content')
              // Clean any remaining actions text
              let text = content.textContent
              const actIdx = text.indexOf('---actions---')
              if (actIdx !== -1) text = text.substring(0, actIdx).trim()
              content.innerHTML = this.renderMarkdown(text)
              // Add feedback buttons
              this.addFeedbackButtons(assistantBubble, chunk.message_index, chunk.matched_topics)
              // Execute UI actions
              if (chunk.ui_actions) {
                this.executeUiActions(chunk.ui_actions)
              }
            } else if (chunk.type === 'error') {
              assistantBubble.querySelector('.assistant-msg-content').textContent = chunk.content
              assistantBubble.classList.add('assistant-msg-error')
            }
          } catch (e) {
            // Skip malformed JSON chunks
          }
        }
      }
    } catch (e) {
      console.error('Stream error:', e)
      this.addMessage('assistant', 'Sorry, something went wrong. Please try again.')
    } finally {
      this.typingIndicatorTarget.classList.add('d-none')
      this.inputTarget.disabled = false
      this.sendButtonTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  // ─── Programmatic Message ────────────────────────

  sendSuggestedMessage(text) {
    if (!this.openValue) this.toggle()
    setTimeout(() => {
      this.inputTarget.value = text
      this.sendMessage()
    }, 300)
  }

  // ─── UI Actions (Highlight, Scroll) ────────────

  executeUiActions(actions) {
    if (!actions || !Array.isArray(actions)) return

    actions.forEach(action => {
      const el = document.querySelector(action.selector)
      if (!el) return

      switch (action.type) {
        case 'highlight':
          this.highlightElement(el)
          break
        case 'scroll_to':
          this.scrollToElement(el)
          break
        case 'focus':
          el.focus()
          break
      }
    })
  }

  highlightElement(el) {
    // Remove any existing highlights
    document.querySelectorAll('.assistant-highlight-overlay').forEach(o => o.remove())

    const rect = el.getBoundingClientRect()
    const overlay = document.createElement('div')
    overlay.className = 'assistant-highlight-overlay'
    overlay.style.cssText = `
      position: fixed;
      top: ${rect.top - 4}px;
      left: ${rect.left - 4}px;
      width: ${rect.width + 8}px;
      height: ${rect.height + 8}px;
      pointer-events: none;
      z-index: 10000;
    `
    document.body.appendChild(overlay)

    // Auto-remove after 5 seconds
    setTimeout(() => overlay.remove(), 5000)

    // Remove on click anywhere
    const removeOnClick = () => {
      overlay.remove()
      document.removeEventListener('click', removeOnClick)
    }
    document.addEventListener('click', removeOnClick)
  }

  scrollToElement(el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  // ─── Feedback ───────────────────────────────────

  addFeedbackButtons(bubble, messageIndex, matchedTopics) {
    const wrapper = document.createElement('div')
    wrapper.className = 'assistant-feedback'
    wrapper.innerHTML = `
      <button class="assistant-feedback-btn assistant-feedback-positive" title="Helpful" data-rating="positive">
        <i class="fa fa-thumbs-up"></i>
      </button>
      <button class="assistant-feedback-btn assistant-feedback-negative" title="Not helpful" data-rating="negative">
        <i class="fa fa-thumbs-down"></i>
      </button>
    `

    wrapper.querySelectorAll('.assistant-feedback-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const rating = btn.dataset.rating
        this.submitFeedback(rating, messageIndex, matchedTopics, wrapper)
      })
    })

    bubble.querySelector('.assistant-msg-bubble').appendChild(wrapper)
  }

  async submitFeedback(rating, messageIndex, matchedTopics, wrapper) {
    // Replace buttons with confirmation or comment prompt
    if (rating === 'negative') {
      wrapper.innerHTML = `
        <div class="assistant-feedback-form">
          <input type="text" class="assistant-feedback-comment" placeholder="What was wrong? (optional)" />
          <button class="assistant-feedback-submit">Send</button>
        </div>
      `
      const submitBtn = wrapper.querySelector('.assistant-feedback-submit')
      const commentInput = wrapper.querySelector('.assistant-feedback-comment')

      submitBtn.addEventListener('click', async () => {
        await this.sendFeedback(rating, messageIndex, matchedTopics, commentInput.value)
        wrapper.innerHTML = '<span class="assistant-feedback-thanks">Thanks for your feedback!</span>'
      })

      commentInput.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter') {
          await this.sendFeedback(rating, messageIndex, matchedTopics, commentInput.value)
          wrapper.innerHTML = '<span class="assistant-feedback-thanks">Thanks for your feedback!</span>'
        }
      })

      commentInput.focus()
    } else {
      await this.sendFeedback(rating, messageIndex, matchedTopics, null)
      wrapper.innerHTML = '<span class="assistant-feedback-thanks">Thanks!</span>'
    }
  }

  async sendFeedback(rating, messageIndex, matchedTopics, comment) {
    try {
      await this.fetchJSON('/assistant/feedback', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id: this.conversationIdValue,
          message_index: messageIndex,
          rating: rating,
          comment: comment,
          matched_topics: matchedTopics || [],
          page_context: this.buildPageContext()
        })
      })
    } catch (e) {
      console.error('Failed to submit feedback:', e)
    }
  }

  // ─── Message Rendering ─────────────────────────

  addMessage(role, content) {
    const wrapper = document.createElement('div')
    wrapper.className = `assistant-msg assistant-msg-${role}`

    const bubble = document.createElement('div')
    bubble.className = 'assistant-msg-bubble'

    const contentEl = document.createElement('div')
    contentEl.className = 'assistant-msg-content'
    if (content) {
      contentEl.innerHTML = role === 'assistant' ? this.renderMarkdown(content) : this.escapeHtml(content)
    }

    bubble.appendChild(contentEl)
    wrapper.appendChild(bubble)
    this.messageListTarget.appendChild(wrapper)
    this.scrollToBottom()
    return wrapper
  }

  clearMessages() {
    this.messageListTarget.innerHTML = ''
  }

  scrollToBottom() {
    this.messageListTarget.scrollTop = this.messageListTarget.scrollHeight
  }

  // Simple markdown: **bold**, [links](url), - lists, numbered lists
  renderMarkdown(text) {
    if (!text) return ''
    let html = this.escapeHtml(text)
    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
    // Numbered lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li>$2</li>')
    // Bullet lists
    html = html.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>')
    // Wrap consecutive <li> in <ol> or <ul>
    html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    // Line breaks
    html = html.replace(/\n/g, '<br>')
    // Clean up <br> inside lists
    html = html.replace(/<br><ul>/g, '<ul>')
    html = html.replace(/<\/ul><br>/g, '</ul>')
    return html
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // ─── Context Building ──────────────────────────

  buildContext() {
    const bodyContext = document.body.dataset.assistantContext
    try {
      return bodyContext ? JSON.parse(bodyContext) : {}
    } catch (e) {
      return {}
    }
  }

  buildPageContext() {
    const activeTab = document.querySelector('.nav-link.active')
    const errors = [...document.querySelectorAll('.alert-danger, .invalid-feedback, .field_with_errors')]
      .map(el => el.textContent.trim())
      .filter(t => t.length > 0 && t.length < 200)
      .slice(0, 5)

    const visibleElements = [...document.querySelectorAll('[data-assistant]')]
      .slice(0, 30)
      .map(el => ({
        name: el.dataset.assistant,
        text: el.textContent.trim().substring(0, 50),
        selector: `[data-assistant="${el.dataset.assistant}"]`
      }))

    // Also grab prominent buttons
    const buttons = [...document.querySelectorAll('.btn:not(.btn-close):not(.assistant-msg-bubble .btn)')]
      .filter(el => el.offsetParent !== null) // visible only
      .slice(0, 15)
      .map(el => ({
        text: el.textContent.trim().substring(0, 50),
        selector: el.id ? `#${el.id}` : null
      }))
      .filter(b => b.text && b.selector)

    return {
      path: window.location.pathname,
      page_title: document.title,
      active_tab: activeTab ? activeTab.textContent.trim() : null,
      visible_errors: errors.length > 0 ? errors : null,
      visible_elements: visibleElements.concat(buttons)
    }
  }

  // ─── State Persistence ─────────────────────────

  persistState() {
    try {
      localStorage.setItem('givsum_assistant', JSON.stringify({
        open: this.openValue,
        conversationId: this.conversationIdValue,
        lastPath: window.location.pathname
      }))
    } catch (e) {
      // localStorage unavailable
    }
  }

  restoreState() {
    try {
      const state = JSON.parse(localStorage.getItem('givsum_assistant') || '{}')
      if (state.open) {
        this.openValue = true
        this.panelTarget.classList.add('assistant-panel-open')
        this.toggleButtonTarget.classList.add('assistant-button-hidden')
      }
      if (state.conversationId) {
        this.conversationIdValue = state.conversationId
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // ─── Helpers ───────────────────────────────────

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  async fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        ...options.headers
      }
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  }
}
